/** Shape of structured error responses the API returns (see server route handlers). */
export interface ApiErrorPayload {
  error?: string;
  code?: string;
  message?: string;
  [key: string]: unknown;
}

/**
 * Typed error for a request the API answered with a failure status.
 * Carries the server's own message, so a caller can tell it apart from a
 * transport error that has no message worth showing.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly data?: ApiErrorPayload;
  /** The AUTAR-XXXX-XXXX handle to quote to support, where the API sent one. */
  readonly supportReference?: string;
  /**
   * A 401 whose session renewal went unanswered (network error, 429, 5xx),
   * so the session may well still be alive.
   */
  readonly sessionUnconfirmed: boolean;

  constructor(
    message: string,
    status: number,
    code?: string,
    data?: ApiErrorPayload,
    supportReference?: string,
    sessionUnconfirmed = false,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.data = data;
    this.supportReference = supportReference;
    this.sessionUnconfirmed = sessionUnconfirmed;
  }
}

/**
 * Statuses whose body names something the caller can change: what they sent,
 * who they are, an account that already exists, or the plan the estate is on.
 * Retrying one unchanged cannot succeed, and support has nothing to look up —
 * the request was answered, not dropped.
 */
const ACTIONABLE_STATUSES = new Set([400, 401, 402, 409]);

/** A 403 is operational by default; an unverified email is the reader's to fix. */
const ACTIONABLE_CODES = new Set(["EMAIL_NOT_VERIFIED"]);

export function isActionableFailure(status: number, code?: string): boolean {
  return (
    ACTIONABLE_STATUSES.has(status) ||
    (code !== undefined && ACTIONABLE_CODES.has(code))
  );
}

export function messageFromPayload(
  payload: ApiErrorPayload,
  status: number,
): string {
  return payload.message ?? payload.error ?? `Request failed (${status})`;
}

/**
 * The message a user sees, with the support reference on it.
 *
 * Appended to the message rather than surfaced through a new component,
 * because there is no single user-visible error renderer: every screen reads
 * `error.message` for itself. Putting it here reaches all of them at once,
 * which is what the phase-1 criterion asks for — carrying it only on the
 * error object left it visible in the platform-admin diagnostics drawer and
 * nowhere a customer looks.
 *
 * Bare parentheses, with no English label, so it needs no catalogue entry and
 * reads the same in every locale. The AUTAR- prefix identifies it well enough
 * for a user to quote and for support to recognise.
 */
export function withSupportReference(
  message: string,
  supportReference?: string,
): string {
  if (!supportReference) return message;
  if (message.includes(supportReference)) return message;
  return `${message} (${supportReference})`;
}
